import React, { useEffect, useState } from "react";
import { Table, Switch, Avatar, message, Spin, Card } from "antd";
import { UserOutlined } from "@ant-design/icons";
import GetPositionsWithFeaturesApi from '../../api/position-features/GetPositionsWithFeaturesApi';
import BulkUpdatePositionFeaturesApi from '../../api/position-features/BulkUpdatePositionFeaturesApi';
import PermissionService from '../../services/PermissionService';
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
function PositionFeatures() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});

  useEffect(() => {
    fetchPositions();
  }, []);

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const response = await GetPositionsWithFeaturesApi();
      if (response.success) {
        setPositions(response.data);
      } else {
        openNotificationError("bottomRight", "Failed to fetch positions");
      }
    } catch (error) {
      openNotificationError("bottomRight", "Error fetching positions");
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureToggle = async (positionId, featureName, enabled) => {
    const updateKey = `${positionId}-${featureName}`;
    setUpdating(prev => ({ ...prev, [updateKey]: true }));

    try {
      // Find current position data
      const position = positions.find(p => p.positionId === positionId);
      const features = {
        prediction: position.predictionEnabled,
        timeline: position.timelineEnabled,
        timesheet: position.timesheetEnabled,
        subtargets: position.subtargetsEnabled,
        chatbot: position.chatbotEnabled,
        zoho: position.zohoEnabled,
        odoo: position.odooEnabled,
        phrm: position.phrmEnabled,
        whatsapp: position.whatsappEnabled,
        gem: position.gemEnabled,
        planApproval: position.planApprovalEnabled,
        targetApproval: position.targetApprovalEnabled,
      };
      
      // Update the specific feature
      features[featureName] = enabled;

      const response = await BulkUpdatePositionFeaturesApi(positionId, features);
      
      if (response.success) {
        setPositions(prev => 
          prev.map(p => 
            p.positionId === positionId 
              ? { ...p, [`${featureName}Enabled`]: enabled }
              : p
          )
        );
        
        // Force refresh permissions cache
        PermissionService.clearCache();
        
        openNotificationSuccess("bottomRight", "Feature updated successfully");
      } else {
        openNotificationError("bottomRight", response.message || "Failed to update feature");
      }
    } catch (error) {
      openNotificationError("bottomRight", "Error updating feature");
    } finally {
      setUpdating(prev => {
        const newState = { ...prev };
        delete newState[updateKey];
        return newState;
      });
    }
  };

  const columns = [
    {
      title: "Position",
      key: "position",
      fixed: "left",
      width: 250,
      render: (_, record) => (
        <div className="flex items-center">
          <Avatar
            src={record.imageUrl}
            icon={<UserOutlined />}
            size={48}
            className="mr-3 flex-shrink-0"
            style={{ minWidth: '48px', minHeight: '48px' }}
          />
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{record.designationName}</div>
            {record.firstName && record.lastName && (
              <div className="text-sm text-gray-500 truncate">
                {record.firstName} {record.lastName}
              </div>
            )}
            {record.unitName && (
              <div className="text-xs text-gray-400 truncate">
                {record.unitName}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Prediction",
      key: "prediction",
      align: "center",
      render: (_, record) => (
        <Switch
          checked={record.predictionEnabled}
          loading={updating[`${record.positionId}-prediction`]}
          onChange={(checked) => 
            handleFeatureToggle(record.positionId, "prediction", checked)
          }
        />
      ),
    },
    {
      title: "Timeline",
      key: "timeline",
      align: "center",
      render: (_, record) => (
        <Switch
          checked={record.timelineEnabled}
          loading={updating[`${record.positionId}-timeline`]}
          onChange={(checked) => 
            handleFeatureToggle(record.positionId, "timeline", checked)
          }
        />
      ),
    },
    {
      title: "Timesheet",
      key: "timesheet",
      align: "center",
      render: (_, record) => (
        <Switch
          checked={record.timesheetEnabled}
          loading={updating[`${record.positionId}-timesheet`]}
          onChange={(checked) => 
            handleFeatureToggle(record.positionId, "timesheet", checked)
          }
        />
      ),
    },
    {
      title: "Sub-Targets",
      key: "subtargets",
      align: "center",
      render: (_, record) => (
        <Switch
          checked={record.subtargetsEnabled}
          loading={updating[`${record.positionId}-subtargets`]}
          onChange={(checked) => 
            handleFeatureToggle(record.positionId, "subtargets", checked)
          }
        />
      ),
    },
    {
      title: "Chatbot",
      key: "chatbot",
      align: "center",
      render: (_, record) => (
        <Switch
          checked={record.chatbotEnabled}
          loading={updating[`${record.positionId}-chatbot`]}
          onChange={(checked) => 
            handleFeatureToggle(record.positionId, "chatbot", checked)
          }
        />
      ),
    },
    {
      title: "Zoho CRM",
      key: "zoho",
      align: "center",
      render: (_, record) => (
        <Switch
          checked={record.zohoEnabled}
          loading={updating[`${record.positionId}-zoho`]}
          onChange={(checked) => 
            handleFeatureToggle(record.positionId, "zoho", checked)
          }
        />
      ),
    },
    {
      title: "Odoo CRM",
      key: "odoo",
      align: "center",
      render: (_, record) => (
        <Switch
          checked={record.odooEnabled}
          loading={updating[`${record.positionId}-odoo`]}
          onChange={(checked) => 
            handleFeatureToggle(record.positionId, "odoo", checked)
          }
        />
      ),
    },
    {
      title: "PeopleHR",
      key: "phrm",
      align: "center",
      render: (_, record) => (
        <Switch
          checked={record.phrmEnabled}
          loading={updating[`${record.positionId}-phrm`]}
          onChange={(checked) => 
            handleFeatureToggle(record.positionId, "phrm", checked)
          }
        />
      ),
    },
    {
      title: "WhatsApp",
      key: "whatsapp",
      align: "center",
      render: (_, record) => (
        <Switch
          checked={record.whatsappEnabled}
          loading={updating[`${record.positionId}-whatsapp`]}
          onChange={(checked) =>
            handleFeatureToggle(record.positionId, "whatsapp", checked)
          }
        />
      ),
    },
    {
      title: "GEM",
      key: "gem",
      align: "center",
      render: (_, record) => (
        <Switch
          checked={record.gemEnabled !== false}
          loading={updating[`${record.positionId}-gem`]}
          onChange={(checked) =>
            handleFeatureToggle(record.positionId, "gem", checked)
          }
        />
      ),
    },
    {
      title: "Plan Approval",
      key: "planApproval",
      align: "center",
      render: (_, record) => (
        <Switch
          checked={record.planApprovalEnabled !== false}
          loading={updating[`${record.positionId}-planApproval`]}
          onChange={(checked) =>
            handleFeatureToggle(record.positionId, "planApproval", checked)
          }
        />
      ),
    },
    {
      title: "Target Approval",
      key: "targetApproval",
      align: "center",
      render: (_, record) => (
        <Switch
          checked={record.targetApprovalEnabled !== false}
          loading={updating[`${record.positionId}-targetApproval`]}
          onChange={(checked) =>
            handleFeatureToggle(record.positionId, "targetApproval", checked)
          }
        />
      ),
    },
  ];

  return (
    <div className="p-6">
      <Card>
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2">Position Features</h2>
          <p className="text-gray-600">
            Enable or disable features for specific positions. Changes take effect immediately.
          </p>
        </div>

        <Table
          columns={columns}
          dataSource={positions}
          rowKey="positionId"
          loading={loading}
          scroll={{ 
            y: 500,
            x: 1600 
          }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} positions`,
            pageSizeOptions: ['10', '15', '25', '50'],
          }}
        />
      </Card>
    </div>
  );
}

export default PositionFeatures;
